import {produce} from 'immer'
import {AxiosResponse} from 'axios'

import {
  PoliciesItemsTypes,
  PoliciesLastEvaluatedKeyTypes,
  PolicyBodyTypes,
  PolicyDetailsTypes,
  AssignedPolicyTypes,
  ExceptionType,
  CloudAppListTypes,
  CloudAppResponseType,
  PolicyNamePayloadType,
  PoliciesClashesPayloadInterface,
  PoliciesClashesInterface,
  UnknownObject,
  UpdatePolicyExceptionsPayload,
  GenericMessageResponse,
  UpdateUrlFiltersPayload,
  DeleteCategoriesPayload,
  CreateCustomCategoriesPayload,
  UpdatePolicyRestrictionsPayload,
  AssignPolicyPayload,
  BypassDataServer,
  BypassDataServerRequest,
  GetDLPConfigResponse,
  UpdateDLPRestrictionsPayload,
  UpdateDLPExceptionsPayload,
  GetPolicyTesterAPIParams,
  PolicyTesterResponse,
  GetDLPBypassResponse,
  UpdateDLPBypassPayload,
  GetSSLInspectionConfigResponse,
  UpdateSSLInspectionPayload,
} from 'interfaces'
import {unwrapAPIError} from 'utils'
import {getFlightdeckBaseUrl, requestHandler} from '../HTTP'
import {EndPoints, HttpMethods} from '../HTTP/HTTP.types'

const flightDeckBaseUrl = getFlightdeckBaseUrl()

const getPolicies = async (data: {
  LastEvaluatedKey?: PoliciesLastEvaluatedKeyTypes
}): Promise<
  AxiosResponse<{
    body: {
      Items: PoliciesItemsTypes[]
      LastEvaluatedKey: PoliciesLastEvaluatedKeyTypes
    }
  }>
> => {
  try {
    return await requestHandler<{
      body: {
        Items: PoliciesItemsTypes[]
        LastEvaluatedKey: PoliciesLastEvaluatedKeyTypes
      }
    }>({
      method: HttpMethods.POST,
      url: EndPoints.GetPolicies,
      data,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getPolicyDetails = async (
  policyName: string,
): Promise<AxiosResponse<PolicyDetailsTypes>> => {
  try {
    return await requestHandler<{
      message: string
      body: PolicyBodyTypes
    }>({
      method: HttpMethods.GET,
      url: EndPoints.GetPolicyDetails,
      options: {
        params: {
          policyName,
          globalcc: true,
        },
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const createPolicy = async (
  policyName: string,
): Promise<
  AxiosResponse<{
    message: string
    body: PolicyBodyTypes
  }>
> => {
  try {
    return await requestHandler<{
      message: string
      body: PolicyBodyTypes
    }>({
      method: HttpMethods.POST,
      url: EndPoints.CreatePolicy,
      data: {policyName},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updatePolicyExceptions = async (
  payload: UpdatePolicyExceptionsPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.POST,
      url: `${flightDeckBaseUrl}/policies/${payload.policyName}${EndPoints.UpdatePolicyExceptions}`,
      data: {data: payload.data} as UnknownObject,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updatePolicyRestrictions = async ({
  policyName,
  data,
}: UpdatePolicyRestrictionsPayload): Promise<
  AxiosResponse<GenericMessageResponse>
> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.POST,
      url: `${flightDeckBaseUrl}/policies/${policyName}${EndPoints.Restrictions}`,
      data: {data},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updatePolicyURLFilters = async ({
  data,
}: UpdateUrlFiltersPayload): Promise<AxiosResponse<{message: string}>> => {
  try {
    return await requestHandler({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}/custom_categories/urls`,
      data: {data},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const deleteCategories = async ({
  data,
}: DeleteCategoriesPayload): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler({
      method: HttpMethods.DELETE,
      url: `${flightDeckBaseUrl}/custom_categories`,
      data: {data},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const createCustomCategories = async ({
  data,
}: CreateCustomCategoriesPayload): Promise<
  AxiosResponse<GenericMessageResponse>
> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.POST,
      url: `${flightDeckBaseUrl}${EndPoints.CustomCategories}`,
      data: {data},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getBypass = async (payload: {
  policyName: string
}): Promise<AxiosResponse<BypassDataServer>> => {
  try {
    return await requestHandler<BypassDataServer>({
      method: HttpMethods.GET,
      url: `${EndPoints.GetBypass}${encodeURIComponent(
        payload.policyName,
      )}` as unknown as EndPoints,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateBypass = async (payload: {
  policyName: string
  data: BypassDataServerRequest
}): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.POST,
      url: `${EndPoints.UpdateBypass}${encodeURIComponent(
        payload.policyName,
      )}` as unknown as EndPoints,
      data: payload.data as UnknownObject,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const deletePolicy = async (payload: {
  policyName: string
}): Promise<AxiosResponse<{message: string}>> => {
  try {
    return await requestHandler<{
      message: string
    }>({
      method: HttpMethods.DELETE,
      url: EndPoints.DeletePolicy,
      data: payload,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getPolicyAssignee = async (
  policyName: string,
): Promise<
  AxiosResponse<{
    message: string
    Items: AssignedPolicyTypes[]
  }>
> => {
  try {
    const res = await requestHandler<{
      message: string
      Groups: {
        emailId: string
        name: string
        membersCount: string
      }[]
      Users: {
        emailId: string
        name: string
      }[]
    }>({
      method: HttpMethods.GET,
      url: `${EndPoints.GetPolicyAssignee}?policyName=${encodeURIComponent(
        policyName,
      )}` as unknown as EndPoints,
    })
    const {Groups, Users, message} = res.data
    const data = {
      message,
      Items: [
        ...Groups.map(obj => ({
          ...obj,
          type: ExceptionType.Group,
        })),
        ...Users.map(obj => ({
          ...obj,
          type: ExceptionType.User,
        })),
      ],
    }
    return {...res, data}
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const assignPolicy = async (
  payload: AssignPolicyPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.POST,
      url: EndPoints.AssignPolicy,
      data: {...payload},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getFlightdeckPolicyTesterDetails = async (
  params: GetPolicyTesterAPIParams,
): Promise<PolicyTesterResponse> => {
  try {
    const {data} = await requestHandler<PolicyTesterResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}${EndPoints.PoliciesTester}`,
      options: {
        params,
      },
    })
    return data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateCloudApp = async (payload: {
  policyName: string
  cac: CloudAppListTypes | string
}): Promise<AxiosResponse<{message: string}>> => {
  try {
    return await requestHandler<{message: string}>({
      method: HttpMethods.POST,
      url: EndPoints.UpdateCac,
      data: payload,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getCloudApp = async (
  payload: PolicyNamePayloadType,
): Promise<AxiosResponse<CloudAppResponseType>> => {
  try {
    const response = await requestHandler<CloudAppResponseType>({
      method: HttpMethods.GET,
      url: `${EndPoints.GetCloudApp}${encodeURIComponent(
        payload.policyName,
      )}` as unknown as EndPoints,
    })
    // TODO: Remove this once backend has removed consumer_info from the response
    const responseData = produce(response, draft => {
      delete draft.data.cac.google?.consumer_info
      delete draft.data.cac.o365?.consumer_info
    })
    return responseData
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getPoliciesClashes = async ({
  policyName,
  lastEvaluatedKey,
}: PoliciesClashesPayloadInterface): Promise<
  AxiosResponse<PoliciesClashesInterface>
> => {
  try {
    return await requestHandler<PoliciesClashesInterface>({
      method: HttpMethods.POST,
      url: `${EndPoints.GetPolicyClashGroups}?policyName=${encodeURIComponent(
        policyName,
      )}` as unknown as EndPoints,
      data: lastEvaluatedKey,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getDLPConfig = async (
  policyName: string,
): Promise<GetDLPConfigResponse> => {
  try {
    const {data} = await requestHandler<GetDLPConfigResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(policyName)}${
        EndPoints.DLP
      }`,
    })
    return data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getDLPBypass = async (
  policyName: string,
): Promise<GetDLPBypassResponse> => {
  try {
    const {data} = await requestHandler<GetDLPBypassResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(policyName)}${
        EndPoints.DLPBypass
      }`,
    })
    return data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateDLPBypass = async (
  policyName: string,
  payload: UpdateDLPBypassPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(policyName)}${
        EndPoints.DLPBypass
      }`,
      data: {...payload},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateDLPRestrictions = async (
  policyName: string,
  payload: UpdateDLPRestrictionsPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(policyName)}${
        EndPoints.DLPRestrictions
      }`,
      data: {data: payload},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateDLPExceptions = async (
  policyName: string,
  payload: UpdateDLPExceptionsPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(policyName)}${
        EndPoints.DLPExceptions
      }`,
      data: {data: payload},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getSSLInspection = async (
  policyName: string,
): Promise<GetSSLInspectionConfigResponse> => {
  try {
    const {data} = await requestHandler<GetSSLInspectionConfigResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}${EndPoints.SSLInspection.replace(
        '{policyName}',
        encodeURIComponent(policyName),
      )}`,
    })
    return data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw errorValue
  }
}

const updateSSLInspection = async (
  policyName: string,
  payload: UpdateSSLInspectionPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}${EndPoints.SSLInspection.replace(
        '{policyName}',
        encodeURIComponent(policyName),
      )}`,
      data: {...payload},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw errorValue
  }
}

export const PoliciesService = {
  getPolicies,
  getPolicyDetails,
  createPolicy,
  updatePolicyExceptions,
  updatePolicyRestrictions,
  updatePolicyURLFilters,
  updateBypass,
  getBypass,
  deletePolicy,
  getPolicyAssignee,
  assignPolicy,
  getFlightdeckPolicyTesterDetails,
  getCloudApp,
  updateCloudApp,
  getPoliciesClashes,
  deleteCategories,
  createCustomCategories,
  getDLPBypass,
  getDLPConfig,
  updateDLPBypass,
  updateDLPExceptions,
  updateDLPRestrictions,
  getSSLInspection,
  updateSSLInspection,
}
